import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { generateWalkthrough, fetchTestPlanJson, testPlanZipUrl, verifyBuildItem, getVerification } from "@/lib/phase3_api";
import { ItemCompetitorCompare, VERDICT_STYLE } from "@/components/CompetitorVerify";
import { toast } from "sonner";
import {
  CheckSquare, ListChecks, Sparkle, Spinner, WarningCircle, Code, Package, ShieldCheck,
} from "@phosphor-icons/react";

function copyText(text) {
  return new Promise((resolve, reject) => {
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        ok ? resolve() : reject(new Error("copy failed"));
      } catch (e) { reject(e); }
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(resolve).catch(fallback);
    } else fallback();
  });
}

function VerifyPanel({ analysisId, item, competitors = [] }) {
  const [polling, setPolling] = useState(false);
  const [queueing, setQueueing] = useState(false);

  const { data } = useQuery({
    queryKey: ["verify", analysisId, item.id],
    queryFn: () => getVerification(analysisId, item.id),
    refetchInterval: polling ? 4000 : false,
  });

  const status = data?.status || "idle";
  const v = data?.verification || item.verification || null;
  const inFlight = status === "queued" || status === "running";

  useEffect(() => {
    if (inFlight) {
      setPolling(true);
    } else if (polling) {
      setPolling(false);
      if (status === "complete") toast.success("Live verification complete");
      if (status === "failed") toast.error(data?.error || "Verification failed");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const run = async () => {
    try {
      setQueueing(true);
      await verifyBuildItem(analysisId, item.id);
      setPolling(true);
      toast.info("Checking the live site against every acceptance criterion…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not start verification");
    } finally {
      setQueueing(false);
    }
  };

  return (
    <div className="mt-4 border border-zinc-200 bg-white p-3" data-testid={`verify-panel-${item.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
          <ShieldCheck size={12} /> Live verify
        </div>
        <button
          onClick={run}
          disabled={queueing || inFlight}
          data-testid={`verify-button-${item.id}`}
          className="border border-zinc-950 bg-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-zinc-950 hover:text-white transition-colors disabled:opacity-60 flex items-center gap-1.5"
        >
          {inFlight || queueing ? <Spinner size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
          {inFlight ? (status === "queued" ? "Queued…" : "Checking live site…") : v ? "Re-verify" : "Verify against live site"}
        </button>
      </div>

      {!v && !inFlight && (
        <div className="mt-2 font-mono text-[10px] text-zinc-500" data-testid={`verify-hint-${item.id}`}>
          Fetches {item.title ? "the" : ""} target's public pages right now and rules pass / fail / unclear on every acceptance criterion.
        </div>
      )}

      {status === "failed" && data?.error && (
        <div className="mt-2 border border-red-300 bg-red-50 p-2 text-sm text-red-800" data-testid={`verify-error-${item.id}`}>
          {data.error}
        </div>
      )}

      {v && (
        <div className="mt-3" data-testid={`verify-result-${item.id}`}>
          <div className="flex items-center gap-2 flex-wrap font-mono text-[10px] uppercase tracking-widest">
            <span className="border border-emerald-300 bg-emerald-100 text-emerald-900 px-2 py-0.5" data-testid={`verify-passed-${item.id}`}>
              {v.passed} pass
            </span>
            <span className="border border-red-300 bg-red-100 text-red-900 px-2 py-0.5" data-testid={`verify-failed-${item.id}`}>
              {v.failed} fail
            </span>
            <span className="border border-amber-300 bg-amber-100 text-amber-900 px-2 py-0.5" data-testid={`verify-unclear-${item.id}`}>
              {v.unclear} unclear
            </span>
            <span className="text-zinc-400 normal-case tracking-normal">
              vs {v.target_url} · {v.checked_pages?.length || 0} live pages
            </span>
          </div>

          {v.summary && <p className="mt-2 text-sm text-zinc-800 leading-relaxed">{v.summary}</p>}

          <ul className="mt-3 space-y-2">
            {(v.checks || []).map((c, i) => {
              const st = VERDICT_STYLE[c.verdict] || VERDICT_STYLE.unclear;
              return (
                <li key={i} className="border border-zinc-200 p-2.5" data-testid={`verify-check-${item.id}-${i}`}>
                  <div className="flex items-start gap-2">
                    <span className={`border px-1.5 py-0.5 font-mono text-[9px] tracking-widest shrink-0 ${st.cls}`}>
                      {st.label}
                    </span>
                    <span className="text-sm text-zinc-900">{c.criterion}</span>
                  </div>
                  {c.evidence && (
                    <div className="mt-1.5 pl-1 text-sm text-zinc-600">
                      {c.evidence}
                      {c.evidence_url && (
                        <a
                          href={c.evidence_url}
                          target="_blank"
                          rel="noreferrer"
                          className="ml-1 font-mono text-[10px] text-zinc-500 hover:underline break-all"
                        >
                          [{c.evidence_url}]
                        </a>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="mt-2 font-mono text-[10px] text-zinc-500">
            Public surfaces only — anything behind a login is reported as unclear, never guessed.
          </div>
        </div>
      )}

      <ItemCompetitorCompare analysisId={analysisId} item={item} competitors={competitors} />
    </div>
  );
}

export function WalkthroughBlock({ analysisId, item, competitors = [], onUpdated }) {
  const [busy, setBusy] = useState(false);
  const wt = item.walkthrough;

  const run = async () => {
    try {
      setBusy(true);
      const result = await generateWalkthrough(analysisId, item.id);
      onUpdated(result);
      toast.success("Walkthrough generated");
    } catch (e) {
      toast.error(e.message || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  if (!wt) {
    return (
      <div className="mt-4 border-t border-zinc-100 pt-4">
        <button
          onClick={run}
          disabled={busy}
          data-testid={`generate-walkthrough-${item.id}`}
          className="border border-zinc-950 bg-white text-zinc-950 px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-950 hover:text-white transition-colors flex items-center gap-2 disabled:opacity-60"
        >
          {busy ? <Spinner size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />}
          {busy ? "Generating…" : "Generate build walkthrough"}
        </button>
        <div className="mt-1.5 font-mono text-[10px] text-zinc-500">
          Milestones, acceptance criteria and definition-of-done, tailored to your industry lens.
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 border-t border-zinc-100 pt-4" data-testid={`walkthrough-${item.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          Build walkthrough
        </div>
        <button
          onClick={run}
          disabled={busy}
          className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-900 flex items-center gap-1"
          data-testid={`regenerate-walkthrough-${item.id}`}
        >
          {busy ? <Spinner size={10} className="animate-spin" /> : <Sparkle size={10} />} Regenerate
        </button>
      </div>

      {wt.overview && (
        <p className="text-sm text-zinc-800 leading-relaxed mb-4 border-l-2 border-zinc-950 pl-3">
          {wt.overview}
        </p>
      )}

      {wt.prerequisites?.length > 0 && (
        <div className="mb-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
            Prerequisites
          </div>
          <ul className="space-y-1 text-sm text-zinc-800">
            {wt.prerequisites.map((p, i) => (
              <li key={i} className="flex items-start gap-2">
                <WarningCircle size={12} className="text-amber-600 mt-1 flex-shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ol className="space-y-3">
        {wt.milestones.map((m, i) => (
          <li key={i} className="border border-zinc-200 bg-white" data-testid={`milestone-${i}`}>
            <div className="flex items-baseline gap-3 px-4 py-2 border-b border-zinc-200 bg-zinc-50">
              <span className="font-mono text-xs text-zinc-500">M{String(i + 1).padStart(2, "0")}</span>
              <span className="font-display font-medium text-zinc-950 flex-1">{m.title}</span>
              {m.estimated_days != null && (
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  ~{m.estimated_days}d
                </span>
              )}
            </div>
            <div className="p-4">
              {m.description && (
                <p className="text-sm text-zinc-700 leading-relaxed mb-3">{m.description}</p>
              )}
              {m.acceptance_criteria?.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1">
                    <ListChecks size={11} /> Acceptance criteria
                  </div>
                  <ul className="space-y-1.5">
                    {m.acceptance_criteria.map((c, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-zinc-800">
                        <CheckSquare size={13} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>

      {wt.definition_of_done?.length > 0 && (
        <div className="mt-4 border border-zinc-950 bg-zinc-950 text-white p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-300 mb-2 flex items-center gap-1">
            <CheckSquare size={11} weight="fill" /> Definition of done
          </div>
          <ul className="space-y-1.5 text-sm">
            {wt.definition_of_done.map((d, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">✓</span>
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <VerifyPanel analysisId={analysisId} item={item} competitors={competitors} />

      {/* Walkthrough As Code — export buttons */}      <div className="mt-4 border border-zinc-200 bg-zinc-50 p-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
          Walkthrough as code
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid={`copy-test-plan-${item.id}`}
            onClick={async () => {
              try {
                const j = await fetchTestPlanJson(analysisId, item.id);
                await copyText(JSON.stringify(j, null, 2));
                toast.success("Test plan JSON copied");
              } catch (e) { toast.error(e.message || "Copy failed"); }
            }}
            className="border border-zinc-300 bg-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-zinc-100 flex items-center gap-1.5"
          >
            <Code size={11} /> Copy JSON test plan
          </button>
          <a
            data-testid={`download-scaffold-${item.id}`}
            href={testPlanZipUrl(analysisId, item.id)}
            className="border border-zinc-950 bg-zinc-950 text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-zinc-800 flex items-center gap-1.5"
          >
            <Package size={11} /> Download Vitest scaffold
          </a>
        </div>
        <div className="mt-2 font-mono text-[10px] text-zinc-500">
          JSON plan is builder-agnostic. Scaffold is a runnable Vitest project — one test file per milestone with <code>test.todo</code> for every criterion.
        </div>
      </div>
    </div>
  );
}
